import { AlertCircle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { forwardRef, useEffect } from 'react';
import { cn } from '../../utils/cn';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  id: string;
  title?: string;
  message: string;
  variant?: ToastVariant;
  duration?: number;
  onClose: (id: string) => void;
}

const variantStyles = {
  success:
    'border-green-500/30 bg-background text-green-700 dark:text-green-400 shadow-lg backdrop-blur-sm',
  error: 'border-destructive/30 bg-background text-destructive shadow-lg backdrop-blur-sm',
  warning:
    'border-yellow-500/30 bg-background text-yellow-700 dark:text-yellow-400 shadow-lg backdrop-blur-sm',
  info: 'border-blue-500/30 bg-background text-blue-700 dark:text-blue-400 shadow-lg backdrop-blur-sm',
};

const variantIcons = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertCircle,
  info: Info,
};

export const Toast = forwardRef<HTMLDivElement, ToastProps>(
  ({ id, title, message, variant = 'info', duration = 5000, onClose }, ref) => {
    const Icon = variantIcons[variant];

    useEffect(() => {
      if (duration > 0) {
        const timer = setTimeout(() => {
          onClose(id);
        }, duration);

        return () => clearTimeout(timer);
      }
    }, [id, duration, onClose]);

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-start gap-3 rounded-lg border p-4 text-sm shadow-lg transition-all duration-300 ease-in-out w-full',
          variantStyles[variant]
        )}
        role="alert"
        aria-live="polite"
      >
        <Icon className="mt-0.5 h-5 w-5 shrink-0" />
        <div className="flex-1 min-w-0 overflow-hidden">
          {title && <div className="font-semibold wrap-break-word">{title}</div>}
          <div className={cn(title ? 'mt-1.5 opacity-90' : '', 'wrap-break-word')}>{message}</div>
        </div>
        <button
          type="button"
          onClick={() => onClose(id)}
          className="shrink-0 rounded-md p-1 text-current opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }
);

Toast.displayName = 'Toast';
